import { A } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  forceHandoff,
  type HandoffResult,
  ISSUE_REASON_MAX_CHARS,
  WEBHOOK_REF_MAX_CHARS,
} from '../lib/issue-actions';
import { createMutation } from '../lib/mutation';
import { orgHref } from '../lib/org';
import { Field } from './Field';
import { buttonClass } from './ui/Button';

// Force a handoff on one issue (arc 101 slice 06). Presenter + its own submit:
// reason (× optional target) → `POST /v1/issues/:id/handoff`.
//
// A 202 here does NOT mean a handoff fired. The route answers `queued: false`
// with a reason when nothing was dispatched — `no_webhook` (no target resolved)
// or `already_pending` (a delivery is already in flight, and the re-force is a
// recorded no-op, never a duplicate dispatch). Each outcome gets its own line;
// only the queued one reads as success. Either way the returned `sessionId` is
// the audit chain that recorded the act, so it's always offered.

export function IssueHandoffForm(props: {
  issueId: string;
  token: string | null;
  /** Fired after a 202 so the parent can re-read server truth. */
  onActed: () => void;
}): JSX.Element {
  const [reason, setReason] = createSignal('');
  const [ref, setRef] = createSignal('');
  const [result, setResult] = createSignal<HandoffResult | null>(null);

  const valid = (): boolean => reason().trim().length > 0;

  const resultKey = (r: HandoffResult): string =>
    r.queued
      ? 'repo_detail.issue_handoff_done'
      : r.reason === 'already_pending'
        ? 'repo_detail.issue_handoff_already'
        : 'repo_detail.issue_handoff_no_webhook';

  const handoff = createMutation({
    call: (input: { reason: string; webhookRef: string | null }) => {
      // Cleared HERE rather than in `submit`, so a click the kit drops as
      // re-entrant leaves the previous verdict on screen instead of blanking it
      // over a write that never started.
      setResult(null);
      return forceHandoff(props.issueId, input, props.token);
    },
    fallback: t('repo_detail.issue_handoff_error'),
    onSuccess: (out) => {
      setResult(out);
      // Keep the reason on a no-op: nothing fired, so the operator is one edit
      // (a target name) away from retrying the same intent.
      if (out.queued) setReason('');
      props.onActed();
    },
  });
  const busy = handoff.pending;
  const error = handoff.error;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid()) return;
    void handoff.run({ reason: reason().trim(), webhookRef: ref().trim() || null });
  };

  return (
    <form class="issue-action" aria-label={t('repo_detail.issue_handoff_title')} onSubmit={submit}>
      <h3 class="issue-action__title">{t('repo_detail.issue_handoff_title')}</h3>
      <p class="issue-action__desc">{t('repo_detail.issue_handoff_desc')}</p>

      <Field
        label={t('repo_detail.issue_reason')}
        id="issue-handoff-reason"
        desc={t('repo_detail.issue_reason_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            rows={3}
            maxlength={ISSUE_REASON_MAX_CHARS}
            disabled={busy()}
            placeholder={t('repo_detail.issue_handoff_reason_placeholder')}
            value={reason()}
            onInput={(e) => setReason(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field
        label={t('repo_detail.issue_handoff_target')}
        id="issue-handoff-target"
        desc={t('repo_detail.issue_handoff_target_desc')}
      >
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="text"
            maxlength={WEBHOOK_REF_MAX_CHARS}
            disabled={busy()}
            placeholder={t('repo_detail.issue_handoff_target_placeholder')}
            value={ref()}
            onInput={(e) => setRef(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={result()}>
        {(r) => (
          <p
            class="issue-action__result"
            classList={{ 'issue-action__warn': !r().queued }}
            role="status"
          >
            {t(resultKey(r()))}{' '}
            <Show when={r().sessionId}>
              {(id) => (
                <A class="issue-action__link" href={orgHref(`/audit/${id()}`)}>
                  {t('repo_detail.issues_view_session')}
                </A>
              )}
            </Show>
          </p>
        )}
      </Show>
      <Show when={error()}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <button type="submit" class={buttonClass({ size: 'sm' })} disabled={!valid() || busy()}>
        {t(busy() ? 'repo_detail.issue_handoff_busy' : 'repo_detail.issue_handoff_submit')}
      </button>
    </form>
  );
}
